/**
 * Which air streams a selection dialog may offer.
 *
 * Air streams are configured per construction class, so a class can withdraw
 * one — but a record that already holds it keeps it: unlike Configuration,
 * Air System, Volume Control and Certification, an air stream is never
 * cleared from a component or accessory when its class drops it. A dialog
 * built from the class list alone therefore could not render its own saved
 * value and showed "- -" over a value still in the database (AHU 7236's
 * opening, 2026-10-27).
 *
 * So: choose from what the class offers, plus whatever THIS SELECT already
 * has. The same grandfathering rule the Special Options dialog uses.
 *
 * ★ ONE SELECT, ONE SAVED VALUE. This used to take a LIST of saved ids, for
 * the energy wheel and the plate heat exchanger, which carry four and eight
 * streams at once. Their dialogs then built ONE list from all of them and
 * fed every dropdown with it — so on AHU 7241, where Class 1 offers Outside
 * and Outside 1 and the wheel holds Supply on the OA side and Exhaust on the
 * RA side, both appeared in all four dropdowns. Grandfathering is a
 * statement about a FIELD, not about a record: call this once per select.
 *
 * `all` is the manufacturer's full list, which is what names the saved one.
 * A missing/empty `offeredIds` means "not scoped" and everything is offered —
 * the per-accessory CMS docs that have not been given a class still answer
 * that way, and must keep working.
 */
export interface AirStreamRow {
  id?: number | string | unknown;
  name?: string | null;
  value?: string | number | null;
  [key: string]: unknown;
}

export const airStreamOptions = (
  all: AirStreamRow[],
  offeredIds: ReadonlyArray<number | string> | undefined | null,
  /** What THIS select already holds, so it can render its own saved value. */
  savedId: number | string | null | undefined
): AirStreamRow[] => {
  if (!offeredIds || offeredIds.length === 0) return all;
  const offered = new Set(offeredIds.map((id) => String(id)));
  const held = savedId == null || savedId === '' ? null : String(savedId);
  return all.filter(
    (row) => offered.has(String(row.id)) || String(row.id) === held);
};
